import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import { hotelInfo } from '../config/hotelTexts';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import logoSvg from '../assets/images/logo-green.png';

const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.white};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: background-color ${({ theme }) => theme.transitions.medium}, box-shadow ${({ theme }) => theme.transitions.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md} 0;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const Logo = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.main};
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const NavLinks = styled.nav`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    background-color: ${({ theme }) => theme.colors.white};
    padding: ${({ theme }) => theme.spacing.md};
    box-shadow: ${({ theme }) => theme.shadows.medium};
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

// Composant NavLink personnalisu00e9 qui ferme le menu mobile lors du clic
const NavLink = ({ to, children, onClick }) => {
  const theme = useContext(ThemeContext);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Du00e9tecter la taille de l'u00e9cran pour savoir si on est sur mobile
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Du00e9terminer si on est sur mobile en fonction du breakpoint tablet
  const isMobile = windowWidth <= parseInt(theme.breakpoints.tablet.replace('px', ''));
  
  const handleClick = (e) => {
    // Si on est sur mobile, fermer le menu
    if (isMobile && onClick) {
      onClick();
    }
  };
  
  return (
    <StyledNavLink to={to} onClick={handleClick}>
      {children}
    </StyledNavLink>
  );
};

const StyledNavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid transparent;
  transition: border-color ${({ theme }) => theme.transitions.fast}, color ${({ theme }) => theme.transitions.fast};
  
  &:hover, &.active {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.secondary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    color: ${({ theme }) => theme.colors.primary};
    padding: ${({ theme }) => theme.spacing.sm} 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.light};
    width: 100%;
    
    &:hover, &.active {
      border-color: ${({ theme }) => theme.colors.accent};
      color: ${({ theme }) => theme.colors.secondary};
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const Header = () => {
  const { t, language } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  return (
    <HeaderContainer scrolled={scrolled}>
      <HeaderContent>
        <Logo to={`/${language}/`} scrolled={scrolled}>
          <img src={logoSvg} alt="Logo Saint-Pierre" style={{width: '36px', height: '36px', marginRight: '12px'}} />
          {hotelInfo.name}
        </Logo>
        
        <MobileMenuButton onClick={toggleMenu} scrolled={scrolled}>
          {isMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <NavLinks isOpen={isMenuOpen}>
          <NavLink to={`/${language}/`} onClick={() => setIsMenuOpen(false)}>{t('nav.home')}</NavLink>
          <NavLink to={`/${language}/${language === 'fr' ? 'chambres' : 'rooms'}`} onClick={() => setIsMenuOpen(false)}>{t('nav.rooms')}</NavLink>
          <NavLink to={`/${language}/restaurant`} onClick={() => setIsMenuOpen(false)}>{t('nav.restaurant')}</NavLink>
          <NavLink to={`/${language}/${language === 'fr' ? 'alentours' : 'surroundings'}`} onClick={() => setIsMenuOpen(false)}>{t('nav.region')}</NavLink>
          <NavLink to={`/${language}/contact`} onClick={() => setIsMenuOpen(false)}>{t('nav.contact')}</NavLink>
          <LanguageSelector />
        </NavLinks>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
